import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrganizations } from '../../hooks/useOrganizations'
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
  Banner,
  ConfirmDialog,
  Table,
  Animate,
  useIsDesktop
} from 'wss3-forge'
import {
  Add20Regular,
  Delete20Regular,
  Edit20Regular,
  Building20Regular,
  Globe20Regular,
  People20Regular,
  Key20Regular
} from '@fluentui/react-icons'
import type { Organization } from '../../types/database'

interface OrganizationWithCounts extends Organization {
  domains_count?: number
  members_count?: number
  sso_connections_count?: number
}

export function Organizations() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { organizations, error, deleteOrganization } = useOrganizations()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<OrganizationWithCounts | null>(null)

  const handleDeleteClick = (org: OrganizationWithCounts) => {
    setSelectedOrg(org)
    setShowDeleteConfirm(true)
  }

  const handleDelete = async () => {
    if (!selectedOrg) return
    await deleteOrganization(selectedOrg.id)
    setShowDeleteConfirm(false)
    setSelectedOrg(null)
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: isDesktop ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <Heading level={2}>Organisations</Heading>
              <Text color="muted">Gérez les organisations et leur SSO entreprise</Text>
            </div>
            <Button icon={<Add20Regular />} onClick={() => navigate('/platform/organizations/new')} style={{ width: isDesktop ? 'auto' : '100%' }}>
              Nouvelle organisation
            </Button>
          </HStack>
        </Animate>

        {error && (
          <Banner variant="error" title="Erreur">
            {error}
          </Banner>
        )}

        <Animate type="slideInUp" delay={100}>
          {organizations.length === 0 ? (
            <Card padding="xl">
              <VStack gap="md" style={{ alignItems: 'center', textAlign: 'center' }}>
                <Building20Regular style={{ fontSize: '48px', color: 'var(--text-muted)' }} />
                <Heading level={4}>Aucune organisation</Heading>
                <Text color="muted">
                  Créez une organisation pour configurer le SSO entreprise pour vos clients.
                </Text>
                <Button icon={<Add20Regular />} onClick={() => navigate('/platform/organizations/new')}>
                  Créer une organisation
                </Button>
              </VStack>
            </Card>
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Organisation',
                  render: (_, row: OrganizationWithCounts) => (
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      {row.logo_url ? (
                        <img
                          src={row.logo_url}
                          alt={row.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-sm)',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: 'var(--brand-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {row.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <Text weight="medium">{row.name}</Text>
                        <Text size="sm" color="muted">{row.slug}</Text>
                      </div>
                    </HStack>
                  )
                },
                {
                  key: 'domains',
                  header: 'Domaines',
                  render: (_, row: OrganizationWithCounts) => (
                    <HStack gap="xs" style={{ alignItems: 'center' }}>
                      <Globe20Regular style={{ color: 'var(--text-muted)' }} />
                      <Text>{row.domains_count || 0}</Text>
                    </HStack>
                  )
                },
                {
                  key: 'members',
                  header: 'Membres',
                  render: (_, row: OrganizationWithCounts) => (
                    <HStack gap="xs" style={{ alignItems: 'center' }}>
                      <People20Regular style={{ color: 'var(--text-muted)' }} />
                      <Text>{row.members_count || 0}</Text>
                    </HStack>
                  )
                },
                {
                  key: 'sso',
                  header: 'Connexions SSO',
                  render: (_, row: OrganizationWithCounts) => (
                    <HStack gap="xs" style={{ alignItems: 'center' }}>
                      <Key20Regular style={{ color: 'var(--text-muted)' }} />
                      <Text>{row.sso_connections_count || 0}</Text>
                    </HStack>
                  )
                },
                {
                  key: 'status',
                  header: 'Statut',
                  render: (_, row: OrganizationWithCounts) => (
                    <Badge variant={row.is_active ? 'success' : 'default'}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  )
                },
                {
                  key: 'actions',
                  header: '',
                  render: (_, row: OrganizationWithCounts) => (
                    <HStack gap="xs">
                      <IconButton
                        icon={<Edit20Regular />}
                        size="sm"
                        onClick={() => navigate(`/platform/organizations/${row.id}`)}
                      />
                      <IconButton
                        icon={<Delete20Regular />}
                        size="sm"
                        onClick={() => handleDeleteClick(row)}
                      />
                    </HStack>
                  )
                }
              ]}
              data={organizations}
              onRowClick={(row) => navigate(`/platform/organizations/${row.id}`)}
            />
          )}
        </Animate>
      </VStack>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Supprimer l'organisation"
        description={`Êtes-vous sûr de vouloir supprimer "${selectedOrg?.name}" ? Cette action supprimera également toutes les connexions SSO et les membres associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  )
}
